import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Alert,
  TextField,
  Chip,
} from '@mui/material';
import { API_BASE_URL } from '../config/environment';
import { useFirebaseAuth } from '../context/FirebaseAuthContext';

export default function StripeConnectAdmin() {
  const { getIdToken } = useFirebaseAuth();
  const [interclubs, setInterclubs] = useState([]);
  const [selectedInterclubId, setSelectedInterclubId] = useState('');
  const [loading, setLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [accountStatus, setAccountStatus] = useState(null);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [accountIdInput, setAccountIdInput] = useState('');

  const normalizeAccountId = (value) => value.trim();
  const isValidAccountId = (value) => value.startsWith('acct_');

  const selectedInterclub = useMemo(
    () => interclubs.find((interclub) => interclub.id === selectedInterclubId),
    [interclubs, selectedInterclubId]
  );

  useEffect(() => {
    const loadInterclubs = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_BASE_URL}/interclubs`);
        const data = await response.json();
        setInterclubs(data || []);
      } catch (err) {
        console.error('Failed to load interclubs:', err);
        setError('Failed to load interclubs');
      } finally {
        setLoading(false);
      }
    };
    loadInterclubs();
  }, []);

  const loadAccountStatus = useCallback(
    async (accountId) => {
      try {
        setStatusLoading(true);
        const token = await getIdToken(true);
        const response = await fetch(`${API_BASE_URL}/connect/accounts/${accountId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (!response.ok) {
          throw new Error('Failed to load account status');
        }
        const data = await response.json();
        setAccountStatus(data);
      } catch (err) {
        console.error('Failed to load Stripe account:', err);
        setError(err.message || 'Failed to load Stripe account');
      } finally {
        setStatusLoading(false);
      }
    },
    [getIdToken]
  );

  useEffect(() => {
    if (!selectedInterclub) {
      setAccountStatus(null);
      setAccountIdInput('');
      return;
    }
    setAccountIdInput(selectedInterclub.stripeAccountId || '');
    if (selectedInterclub.stripeAccountId) {
      loadAccountStatus(selectedInterclub.stripeAccountId);
    } else {
      setAccountStatus(null);
    }
  }, [selectedInterclub, loadAccountStatus]);

  const handleCreateAccount = async () => {
    try {
      setError(null);
      setSuccessMessage('');
      if (!selectedInterclub) return;
      const token = await getIdToken(true);
      const response = await fetch(`${API_BASE_URL}/connect/accounts`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error('Failed to create Stripe account');
      }
      const data = await response.json();
      const newAccountId = data?.id;
      if (!newAccountId) {
        throw new Error('Stripe account ID missing');
      }
      await updateInterclubStripeAccount(newAccountId);
      setAccountIdInput(newAccountId);
      loadAccountStatus(newAccountId);
      setSuccessMessage('Stripe account created successfully.');
      return newAccountId;
    } catch (err) {
      console.error('Create account failed:', err);
      setError(err.message || 'Failed to create account');
      return null;
    }
  };

  const updateInterclubStripeAccount = useCallback(
    async (stripeAccountId) => {
      const token = await getIdToken(true);
      const response = await fetch(`${API_BASE_URL}/interclubs/${selectedInterclub.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ stripeAccountId }),
      });
      if (!response.ok) {
        throw new Error('Failed to update interclub');
      }
      setInterclubs((prev) =>
        prev.map((interclub) =>
          interclub.id === selectedInterclub.id ? { ...interclub, stripeAccountId } : interclub
        )
      );
    },
    [getIdToken, selectedInterclub]
  );

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    const storedState = localStorage.getItem('stripeConnectState');
    const storedInterclubId = localStorage.getItem('stripeConnectInterclubId');

    if (!code) return;

    if (!state || !storedState || state !== storedState) {
      setError('Stripe connection failed. Please try again.');
      return;
    }

    if (!storedInterclubId) {
      setError('Missing interclub selection for Stripe connection.');
      return;
    }

    const finalizeConnection = async () => {
      try {
        setError(null);
        setSuccessMessage('');
        const token = await getIdToken(true);
        const response = await fetch(
          `${API_BASE_URL}/connect/oauth/callback?code=${encodeURIComponent(code)}&interclubId=${encodeURIComponent(
            storedInterclubId
          )}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (!response.ok) {
          throw new Error('Failed to connect Stripe account');
        }
        const data = await response.json();
        if (data?.stripeAccountId) {
          await updateInterclubStripeAccount(data.stripeAccountId);
          setAccountIdInput(data.stripeAccountId);
          loadAccountStatus(data.stripeAccountId);
          setSuccessMessage('Stripe account connected successfully.');
        }
        localStorage.removeItem('stripeConnectState');
        localStorage.removeItem('stripeConnectInterclubId');
        const cleanedUrl = `${window.location.origin}${window.location.pathname}`;
        window.history.replaceState({}, document.title, cleanedUrl);
      } catch (err) {
        console.error('Stripe connect finalize failed:', err);
        setError(err.message || 'Failed to connect Stripe account');
      }
    };

    finalizeConnection();
  }, [getIdToken, loadAccountStatus, updateInterclubStripeAccount]);

  const handleSaveAccountId = async () => {
    try {
      setError(null);
      setSuccessMessage('');
      if (!selectedInterclub) return;
      const normalized = normalizeAccountId(accountIdInput);
      if (!normalized) {
        throw new Error('Stripe account ID is required');
      }
      if (!isValidAccountId(normalized)) {
        throw new Error('Stripe account ID must start with acct_ (not an email).');
      }
      await updateInterclubStripeAccount(normalized);
      loadAccountStatus(normalized);
      setSuccessMessage('Stripe account ID saved.');
    } catch (err) {
      console.error('Update account ID failed:', err);
      setError(err.message || 'Failed to update account ID');
    }
  };

  const handleOnboarding = async () => {
    try {
      setError(null);
      setSuccessMessage('');
      let normalized = normalizeAccountId(accountIdInput);
      if (!normalized || !isValidAccountId(normalized)) {
        const createdAccountId = await handleCreateAccount();
        if (!createdAccountId) {
          throw new Error('Unable to create a Stripe account for onboarding.');
        }
        normalized = createdAccountId;
      }
      const token = await getIdToken(true);
      const response = await fetch(`${API_BASE_URL}/connect/accounts/${normalized}/onboard`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          baseUrl: window.location.origin,
          returnUrl: `${window.location.origin}/admin/connect`,
          refreshUrl: `${window.location.origin}/admin/connect`,
        }),
      });
      if (!response.ok) {
        throw new Error('Failed to create onboarding link');
      }
      const data = await response.json();
      if (data?.url) {
        window.open(data.url, '_blank', 'noopener,noreferrer');
        setSuccessMessage('Onboarding opened in a new tab.');
      }
    } catch (err) {
      console.error('Onboarding failed:', err);
      setError(err.message || 'Failed to start onboarding');
    }
  };

  const handleOauthConnect = async () => {
    try {
      setError(null);
      setSuccessMessage('');
      if (!selectedInterclub) {
        throw new Error('Select an interclub first.');
      }
      const state = Math.random().toString(36).slice(2);
      localStorage.setItem('stripeConnectState', state);
      localStorage.setItem('stripeConnectInterclubId', selectedInterclub.id);

      const token = await getIdToken(true);
      const response = await fetch(`${API_BASE_URL}/connect/oauth?state=${encodeURIComponent(state)}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error('Failed to start Stripe connection');
      }
      const data = await response.json();
      if (data?.url) {
        window.location.assign(data.url);
      } else {
        throw new Error('Stripe connection URL missing');
      }
    } catch (err) {
      console.error('Stripe connect start failed:', err);
      setError(err.message || 'Failed to connect Stripe account');
    }
  };

  return (
    <Paper elevation={2} sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
        Stripe Connect Onboarding
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Create or connect Stripe accounts for interclubs to collect season entry fees.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <FormControl fullWidth sx={{ flex: 1, minWidth: 220 }}>
              <InputLabel>Interclub</InputLabel>
              <Select
                value={selectedInterclubId}
                label="Interclub"
                onChange={(e) => setSelectedInterclubId(e.target.value)}
              >
                <MenuItem value="">
                  <em>Select an interclub...</em>
                </MenuItem>
                {interclubs.map((interclub) => (
                  <MenuItem key={interclub.id} value={interclub.id}>
                    {interclub.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {selectedInterclub && (
              <Chip
                label={selectedInterclub.stripeAccountId ? 'Connected' : 'Not Connected'}
                color={selectedInterclub.stripeAccountId ? 'success' : 'warning'}
                variant={selectedInterclub.stripeAccountId ? 'filled' : 'outlined'}
              />
            )}
          </Box>

          {selectedInterclub && (
            <>
              <TextField
                fullWidth
                label="Stripe Account ID"
                value={accountIdInput}
                onChange={(e) => setAccountIdInput(e.target.value)}
                placeholder="acct_..."
                helperText="Use the Stripe account ID (acct_...). Email addresses will not work."
              />
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button variant="outlined" onClick={handleSaveAccountId}>
                  Save Account ID
                </Button>
                <Button variant="contained" onClick={handleCreateAccount}>
                  Create New Account
                </Button>
                <Button variant="outlined" color="secondary" onClick={handleOauthConnect}>
                  Connect Existing Account
                </Button>
                <Button variant="contained" color="secondary" onClick={handleOnboarding} disabled={!accountIdInput}>
                  Start Onboarding
                </Button>
              </Box>
            </>
          )}

          {statusLoading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={18} />
              <Typography variant="body2">Loading account status...</Typography>
            </Box>
          )}

          {accountStatus && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip
                label={accountStatus.charges_enabled ? 'Charges Enabled' : 'Charges Disabled'}
                color={accountStatus.charges_enabled ? 'success' : 'default'}
              />
              <Chip
                label={accountStatus.payouts_enabled ? 'Payouts Enabled' : 'Payouts Disabled'}
                color={accountStatus.payouts_enabled ? 'success' : 'default'}
              />
              <Chip
                label={accountStatus.details_submitted ? 'Details Submitted' : 'Details Pending'}
                color={accountStatus.details_submitted ? 'success' : 'default'}
              />
            </Box>
          )}
        </Box>
      )}
    </Paper>
  );
}
